import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { CircleIcon } from 'lucide-react';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { cn } from '../../utils/cn';

const radioGroupItemVariants = cva(
    'flex aspect-square shrink-0 cursor-pointer items-center justify-center rounded-full border border-primary-foreground text-primary-foreground shadow-sm outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
    {
        variants: {
            color: {
                default: '',
                brick: 'text-brick-500 [&[data-state=checked]]:text-brick-500',
            },
            background: {
                default: '',
                gray: 'bg-gray-300',
                none: 'bg-transparent',
                transparent: 'bg-transparent',
            },
            size: {
                default: 'size-4',
                small: 'size-2',
                medium: 'size-3.5',
            },
        },
        defaultVariants: {
            color: 'default',
            background: 'default',
            size: 'default',
        },
    },
);

export function RadioGroupItem({
    className,
    color,
    background,
    size,
    iconClasssNames,
    ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item> &
    VariantProps<typeof radioGroupItemVariants> & {
        iconClasssNames?: string;
    }) {
    return (
        <RadioGroupPrimitive.Item
            data-slot="radio-group-item"
            className={cn(
                radioGroupItemVariants({
                    color,
                    background,
                    size,
                    className,
                }),
            )}
            {...props}
        >
            <RadioGroupPrimitive.Indicator
                data-slot="radio-group-indicator"
                className="flex items-center justify-center"
            >
                <CircleIcon
                    className={cn('size-2 fill-current', iconClasssNames)}
                />
            </RadioGroupPrimitive.Indicator>
        </RadioGroupPrimitive.Item>
    );
}
